import { z } from 'zod';
export const PASSWORD_MIN_LENGTH = 8;
/** Upper bound on password length (OWASP ASVS asks for at least 64 to be allowed). */
export const PASSWORD_MAX_LENGTH = 64;
/**
 * bcrypt silently ignores everything after the 72nd byte, so a longer password
 * would verify against a hash of its own prefix. Multi-byte UTF-8 (accents,
 * emoji) can exceed this well under PASSWORD_MAX_LENGTH characters.
 */
export const PASSWORD_MAX_BYTES = 72;
export const PASSWORD_SPECIAL_CHARACTERS = '@$!%*?&';
/**
 * Single source of truth for password rules.
 *
 * The backend validates against this list through `PasswordSchema`, and the
 * register form renders the same list as its live checklist, so the two cannot
 * drift apart. Adding a rule here automatically adds it to both.
 *
 * There is deliberately no allow-list of permitted characters: any character is
 * accepted as long as every requirement below is satisfied.
 */
export const PASSWORD_REQUIREMENTS = [
    { id: 'uppercase', label: 'Uppercase letter', test: password => /[A-Z]/.test(password) },
    { id: 'lowercase', label: 'Lowercase letter', test: password => /[a-z]/.test(password) },
    { id: 'number', label: 'Number', test: password => /\d/.test(password) },
    {
        id: 'length',
        label: `At least ${PASSWORD_MIN_LENGTH} characters`,
        test: password => password.length >= PASSWORD_MIN_LENGTH
    },
    {
        id: 'special',
        label: `Special character (${PASSWORD_SPECIAL_CHARACTERS})`,
        test: password => [...password].some(char => PASSWORD_SPECIAL_CHARACTERS.includes(char))
    }
];
export const getUnmetPasswordRequirements = (password) => PASSWORD_REQUIREMENTS.filter(requirement => !requirement.test(password));
// TextEncoder rather than Buffer: this module is also bundled into the browser.
const utf8ByteLength = (value) => new TextEncoder().encode(value).length;
/**
 * Validates a password against PASSWORD_REQUIREMENTS plus the length caps.
 * The caps are limits rather than "must contain" rules, so they are kept out of
 * the requirement list that drives the register checklist and its n/5 counter.
 */
export const PasswordSchema = z.string().superRefine((password, ctx) => {
    const unmet = getUnmetPasswordRequirements(password);
    if (unmet.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Password must contain: ${unmet.map(requirement => requirement.label).join(', ')}`
        });
    }
    if (password.length > PASSWORD_MAX_LENGTH) {
        ctx.addIssue({
            code: z.ZodIssueCode.too_big,
            type: 'string',
            maximum: PASSWORD_MAX_LENGTH,
            inclusive: true,
            message: `Password must be at most ${PASSWORD_MAX_LENGTH} characters`
        });
    }
    else if (utf8ByteLength(password) > PASSWORD_MAX_BYTES) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Password is too long when encoded: it must be at most ${PASSWORD_MAX_BYTES} bytes of UTF-8, and accented characters or emoji use several bytes each`
        });
    }
});
